import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { clearDates, removeSegment, setDate, setDateMultiCity, setLocation, swapLocations, swapMultiLocations } from '@websky/core/src/SearchForm/store/segments/actions';
import { getSelectedDates, canRemoveSegment as getCanRemoveSegment, isMultiCity as getIsMultiCity, makeGetSegment } from '@websky/core/src/SearchForm/store/segments/selectors';
import { LocationType } from '@websky/core/src/SearchForm/types';
import { ThemeProvider } from '@websky/core/src/theme';
import Button from '@websky/core/src/Button/Button';
import Datepicker from '@websky/core/src/SearchForm/components/SearchForm/Segments/Segment/Datepicker/Datepicker';
import LocationField from '@websky/core/src/SearchForm/components/SearchForm/Segments/Segment/Location/Location';
import { addRecentSearch } from '@websky/core/src/SearchForm/store/recentSearches/actions';
import { getRecentSearches } from '@websky/core/src/SearchForm/store/recentSearches/selectors';
import { useConfig } from '@websky/core/src/context';
import { getPassengersForRequest } from '@websky/core/src/SearchForm/store/passengers/selectors';
import Value from './Value/Value';
import css from './Segment.css';
const SwapIcon = (React.createElement("svg", { width: "22", height: "29", viewBox: "0 0 22 29", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M20 20.5C20.5523 20.5 21 20.9477 21 21.5C21 22.0523 20.5523 22.5 20 22.5V20.5ZM1.29289 22.2071C0.902369 21.8166 0.902369 21.1834 1.29289 20.7929L7.65685 14.4289C8.04738 14.0384 8.68054 14.0384 9.07107 14.4289C9.46159 14.8195 9.46159 15.4526 9.07107 15.8431L3.41421 21.5L9.07107 27.1569C9.46159 27.5474 9.46159 28.1805 9.07107 28.5711C8.68054 28.9616 8.04738 28.9616 7.65685 28.5711L1.29289 22.2071ZM20 22.5H2V20.5H20V22.5Z", fill: "currentColor" }),
    React.createElement("path", { d: "M20.7071 6.79289C21.0976 7.18342 21.0976 7.81658 20.7071 8.20711L14.3431 14.5711C13.9526 14.9616 13.3195 14.9616 12.9289 14.5711C12.5384 14.1805 12.5384 13.5474 12.9289 13.1569L18.5858 7.5L12.9289 1.84315C12.5384 1.45262 12.5384 0.819457 12.9289 0.428932C13.3195 0.0384078 13.9526 0.0384078 14.3431 0.428932L20.7071 6.79289ZM2 8.5C1.44772 8.5 1 8.05228 1 7.5C1 6.94772 1.44772 6.5 2 6.5V8.5ZM20 8.5H2V6.5H20V8.5Z", fill: "currentColor" })));
const RemoveIcon = (React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M17.8332 7.34163L16.6582 6.16663L11.9998 10.825L7.3415 6.16663L6.1665 7.34163L10.8248 12L6.1665 16.6583L7.3415 17.8333L11.9998 13.175L16.6582 17.8333L17.8332 16.6583L13.1748 12L17.8332 7.34163Z", fill: "#BB2649" }),
    React.createElement("circle", { cx: "12", cy: "12", r: "11", stroke: "#BB2649", strokeWidth: "2" })));
const Segment = React.memo(({ segmentId }) => {
    const dispatch = useDispatch();
    const { SearchForm: { showPricesInDatepicker: showPricesInCalendar } } = useConfig();
    const isMultiCity = useSelector(getIsMultiCity);
    const selectedDates = useSelector(getSelectedDates);
    const canRemoveSegment = useSelector(getCanRemoveSegment);
    const recentSearches = useSelector(getRecentSearches);
    const passengers = useSelector(getPassengersForRequest);
    const segment = useSelector(makeGetSegment(segmentId));
    const segmentTheme = {
        SearchForm: {
            Segment: {
                input: css.segment__input,
                input_text: css.segment__inputText
            },
            Location: {
                input_withoutPlaceholder: css.location__inputWithoutPlaceholder,
                autocomplete: css.location__autocomplete,
                input_icon: css.location__inputIcon
            },
            Value: {
                wrapper: css.value__wrapper,
                value: css.value__value,
                code: css.value__code,
                wrapper_alternative: css.value__wrapperAlternative,
                withMultiCity: css.value__withMultiCity
            },
            Datepicker: {
                dates: css.datepicker__dates,
                dates_focused: css.datepicker__datesFocused,
                dates__to: css.datepicker__datesTo,
                dates__back: css.datepicker__datesBack,
                dates__to_focused: css.datepicker__datesToFocused,
                dates__back_focused: css.datepicker__datesBackFocused,
                date__inputWrapper: css.datepicker__dateInputWrapper,
                dow: css.datepicker__dow,
                oneDate: css.datepicker__oneDate
            }
        }
    };
    const onDateChange = React.useCallback((date, isBackDate = false) => {
        if (isMultiCity) {
            dispatch(setDateMultiCity(date, segmentId));
        }
        else {
            dispatch(setDate(date, isBackDate));
            if (!isBackDate) {
                // Reset back date.
                dispatch(setDate(null, true));
            }
        }
    }, [dispatch, segmentId, isMultiCity]);
    const onSetLocation = React.useCallback((location, type) => {
        if (type === LocationType.Departure && location.iata === (segment.arrival && segment.arrival.iata)) {
            dispatch(setLocation(null, LocationType.Arrival));
        }
        if (isMultiCity) {
            dispatch(setLocation(location, type, segmentId));
        }
        else {
            dispatch(setLocation(location, type));
        }
        dispatch(addRecentSearch(location));
    }, [dispatch, segmentId, isMultiCity, segment]);
    const onSwapLocations = React.useCallback(() => {
        if (isMultiCity) {
            dispatch(swapMultiLocations(segmentId));
        }
        else {
            dispatch(swapLocations());
        }
    }, [dispatch, isMultiCity, segmentId]);
    const onRemoveSegment = () => dispatch(removeSegment(segmentId));
    const onClearDates = () => dispatch(clearDates());
    return (React.createElement(ThemeProvider, { value: segmentTheme },
        React.createElement("div", { className: cn(css.container, {
                [css.container_multiCity]: isMultiCity
            }) },
            React.createElement("div", { className: css.wrapper },
                React.createElement("div", { className: cn(css.cell, css.cell_departure) },
                    React.createElement(LocationField, { type: LocationType.Departure, segmentId: segmentId, locations: {
                            departure: segment.departure,
                            arrival: segment.arrival
                        }, setLocation: onSetLocation, recentSearches: recentSearches, withMultiCity: isMultiCity })),
                React.createElement("div", { className: cn(css.cell, css.cell_swap) },
                    React.createElement(Button, { className: css.swap, onClick: onSwapLocations }, SwapIcon)),
                React.createElement("div", { className: cn(css.cell, css.cell_arrival) },
                    React.createElement(LocationField, { type: LocationType.Arrival, segmentId: segmentId, locations: {
                            departure: segment.departure,
                            arrival: segment.arrival
                        }, setLocation: onSetLocation, recentSearches: recentSearches, withMultiCity: isMultiCity })),
                React.createElement("div", { className: cn(css.cell, css.cell_date) },
                    React.createElement(Datepicker, { passengers: passengers, dateTo: segment.date, dateBack: segment.dateBack, selectedDates: selectedDates, onDateChange: onDateChange, withDateBack: !isMultiCity, showPrices: showPricesInCalendar, clearDates: onClearDates, segmentId: segmentId, locations: {
                            departure: segment.departure,
                            arrival: segment.arrival
                        }, renderValue: props => React.createElement(Value, Object.assign({}, props)) }))),
            isMultiCity && canRemoveSegment && (React.createElement("div", { className: css.remove, onClick: onRemoveSegment }, RemoveIcon)))));
}, (prevProps, nextProps) => prevProps.segmentId === nextProps.segmentId);
export default Segment;
